import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, SearchIcon, CheckIcon, Globe } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useOverlayHost } from '@/components/ui/overlay-host';

const sizeClasses = {
    sm: 'h-8 px-2.5 text-[0.8125rem]',
    default: 'h-9 px-3',
    lg: 'h-10 px-3 text-base',
} as const;

interface TimezoneSelectProps {
    value?: string;
    onChange: (value: string) => void;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    size?: 'sm' | 'default' | 'lg';
    invalid?: boolean;
}

function getUtcOffsetLabel(timeZone: string): string {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: 'longOffset',
        }).formatToParts(new Date());
        const offset = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
        if (!offset) return 'UTC';
        const normalized = offset.replace('GMT', 'UTC');
        return normalized === 'UTC' ? 'UTC+00:00' : normalized;
    } catch {
        return 'UTC';
    }
}

function formatTimezoneLabel(timeZone: string, offset: string): string {
    return `(${offset}) ${timeZone}`;
}

/** Falls back to UTC where `Intl` is unavailable or throws (older embedded browsers). */
function browserTimezoneFallback(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
}

/**
 * `Intl.supportedValuesOf` is missing on some engines — an unguarded call
 * throws and blanks every page that renders this component (13+ consumers).
 * Falls back to a minimal list containing at least the browser's own
 * timezone and `'UTC'`.
 */
function supportedTimezones(): readonly string[] {
    try {
        return Intl.supportedValuesOf('timeZone');
    } catch {
        return [...new Set([browserTimezoneFallback(), 'UTC'])];
    }
}

export function TimezoneSelect({
    value,
    onChange,
    className,
    placeholder,
    disabled = false,
    size = 'default',
    invalid = false,
}: TimezoneSelectProps) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    // The row the arrow keys are on. Focus stays in the search box, so the
    // reader can keep typing while moving through the list.
    const [activeIndex, setActiveIndex] = React.useState(0);
    const listRef = React.useRef<HTMLDivElement>(null);
    /** Centre the chosen zone when the list opens; afterwards only nudge. */
    const scrollModeRef = React.useRef<ScrollLogicalPosition>('center');
    // Inside a Sheet/Dialog the list mounts in that modal, not `<body>` — out
    // there the modal's scroll lock swallowed the wheel and the list could not
    // be scrolled.
    const overlayHost = useOverlayHost();

    const resolvedPlaceholder = placeholder ?? t('common.selectTimezone', 'Select timezone…');

    const timezones = React.useMemo(
        () =>
            supportedTimezones().map((tz) => ({
                tz,
                offset: getUtcOffsetLabel(tz),
            })),
        [],
    );

    const filteredTimezones = React.useMemo(() => {
        if (!search) return timezones;
        const q = search.toLowerCase();
        return timezones.filter(
            ({ tz, offset }) => tz.toLowerCase().includes(q) || offset.toLowerCase().includes(q),
        );
    }, [search, timezones]);

    const selectedOffset = value ? getUtcOffsetLabel(value) : '';

    const handleSelect = (tz: string) => {
        onChange(tz);
        setOpen(false);
        setSearch('');
    };

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        if (!next) {
            setSearch('');
            return;
        }
        scrollModeRef.current = 'center';
        setActiveIndex(
            Math.max(
                0,
                timezones.findIndex(({ tz }) => tz === value),
            ),
        );
    };

    // Keep the active row in view as the arrow keys move it.
    React.useEffect(() => {
        if (!open) return;
        const row = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
        // `scrollIntoView` is missing in jsdom; guard so tests and old engines are safe.
        row?.scrollIntoView?.({ block: scrollModeRef.current });
        scrollModeRef.current = 'nearest';
    }, [open, activeIndex]);

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const last = filteredTimezones.length - 1;
        if (last < 0) return;
        const page = 8;
        const moves: Partial<Record<string, (index: number) => number>> = {
            ArrowDown: (index) => Math.min(last, index + 1),
            ArrowUp: (index) => Math.max(0, index - 1),
            PageDown: (index) => Math.min(last, index + page),
            PageUp: (index) => Math.max(0, index - page),
        };
        const move = moves[event.key];
        if (move) {
            event.preventDefault();
            setActiveIndex(move);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const item = filteredTimezones[Math.min(activeIndex, last)];
            if (item) handleSelect(item.tz);
        }
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange} modal>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-invalid={invalid || undefined}
                    data-slot="timezone-select-trigger"
                    disabled={disabled}
                    className={cn(
                        'w-full justify-between gap-2 bg-bg-surface font-normal',
                        sizeClasses[size],
                        invalid && 'border-error-500',
                        className,
                    )}
                >
                    {value ? (
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                            <Globe className="size-4 shrink-0 text-text-tertiary" />
                            <span className="truncate text-text-primary">
                                {formatTimezoneLabel(value, selectedOffset)}
                            </span>
                        </span>
                    ) : (
                        <span className="flex items-center gap-2 text-text-tertiary">
                            <Globe className="size-4 shrink-0" />
                            {resolvedPlaceholder}
                        </span>
                    )}
                    <ChevronDownIcon
                        className={cn(
                            'ml-2 size-4 shrink-0 text-text-tertiary transition-transform',
                            open && 'rotate-180',
                        )}
                        aria-hidden="true"
                    />
                </Button>
            </PopoverTrigger>

            <PopoverContent
                className="w-(--radix-popover-trigger-width) min-w-72 gap-0 p-0"
                align="start"
                sideOffset={6}
                container={overlayHost ?? undefined}
            >
                <div className="border-b border-border-default px-1">
                    <Input
                        startIcon={<SearchIcon />}
                        placeholder={t('common.searchTimezone', 'Search timezone…')}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setActiveIndex(0);
                        }}
                        onKeyDown={handleSearchKeyDown}
                        className="h-8 rounded-none border-0 shadow-none focus-visible:shadow-none focus-visible:border-0"
                        data-size="sm"
                    />
                </div>
                <div ref={listRef} className="max-h-60 overflow-y-auto overscroll-contain p-1 py-0">
                    {filteredTimezones.length === 0 ? (
                        <div className="py-4 text-center text-sm text-text-tertiary">
                            {t('common.noTimezonesFound', 'No timezones found.')}
                        </div>
                    ) : (
                        filteredTimezones.map(({ tz, offset }, index) => {
                            const isSelected = tz === value;
                            const isActive = index === activeIndex;
                            return (
                                <Button
                                    key={tz}
                                    type="button"
                                    variant="ghost"
                                    data-index={index}
                                    data-active={isActive || undefined}
                                    // Not a tab stop: the arrow keys from the search box
                                    // reach every row, so tabbing through hundreds is noise.
                                    tabIndex={-1}
                                    onClick={() => handleSelect(tz)}
                                    onMouseMove={() => setActiveIndex(index)}
                                    className={cn(
                                        'flex h-auto w-full cursor-default items-center justify-start gap-2.5 rounded-sm px-2 py-2 text-sm font-normal',
                                        isActive && 'bg-bg-hover',
                                        isSelected &&
                                            'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300',
                                    )}
                                >
                                    <Globe className="size-4 shrink-0 text-text-tertiary" />
                                    <span className="flex-1 truncate text-left text-text-primary">
                                        {formatTimezoneLabel(tz, offset)}
                                    </span>
                                    {isSelected && (
                                        <CheckIcon className="size-4 shrink-0 text-primary-500" />
                                    )}
                                </Button>
                            );
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
